import { DeleteOutlined } from "@ant-design/icons";
import { Button, Drawer, Form, Input, Popconfirm, Row, Space, Typography, message, theme } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { API_BASE_URL } from "../../config/serverApiConfig";
import { weekdayKu } from "../../utils/surgeryAppointments";
import { AptDatePicker, AptTimePicker } from "./AptPickers";
import "./styles.css";

const { Title, Text } = Typography;

function formatTime(time) {
  if (!time) return "";
  const parsed = dayjs(String(time).slice(0, 5), "HH:mm");
  return parsed.isValid() ? parsed.format("h:mm A") : String(time).slice(0, 5);
}

function formatDay(date, time) {
  if (!date) return "—";
  const clock = formatTime(time);
  return `${dayjs(date).format("YYYY/MM/DD")} · ${weekdayKu(date)}${clock ? ` · ${clock}` : ""}`;
}

function isBetweenVisits(current, qalib, taqal) {
  if (!current || !qalib || !taqal) return false;
  return current.isAfter(dayjs(qalib), "day") && current.isBefore(dayjs(taqal), "day");
}

function firstDayBetween(qalib, taqal) {
  if (!qalib || !taqal) return null;
  const start = dayjs(qalib).add(1, "day").startOf("day");
  const end = dayjs(taqal).subtract(1, "day").startOf("day");
  if (start.isAfter(end, "day")) return null;
  const today = dayjs().startOf("day");
  if (!today.isBefore(start) && !today.isAfter(end)) return today;
  return start;
}

function Visit({ onTabCount }) {
  const { id } = useParams();
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [visible, setVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const visitWindow = useMemo(() => {
    const mine = (bookings || []).filter((item) => String(item.patient?.id) === String(id));
    const latest = (dates) => dates.filter(Boolean).sort((a, b) => dayjs(b).valueOf() - dayjs(a).valueOf())[0] || null;
    const qalib = latest(mine.map((item) => item.qalibDate));
    const taqal = latest(mine.map((item) => item.taqalDate));
    if (!qalib || !taqal) return null;
    return { qalib, taqal };
  }, [bookings, id]);

  useEffect(() => {
    axios.get(`${API_BASE_URL}history/${id}`).then((res) => {
      setData(res.data || []);
    });
    axios.get(`${API_BASE_URL}appointment`).then((res) => {
      setBookings(res.data || []);
    }).catch(() => setBookings([]));
  }, [id]);

  const openForm = () => {
    const suggested = firstDayBetween(visitWindow?.qalib, visitWindow?.taqal) || dayjs();
    const clock = dayjs().format("HH:mm");
    form.setFieldsValue({
      date: suggested,
      visitTime: clock,
      title: "",
    });
    setVisible(true);
  };

  const submit = (values) => {
    setConfirmLoading(true);
    axios
      .post(`${API_BASE_URL}history`, {
        date: values.date.format("YYYY-MM-DD"),
        visitTime: values.visitTime || null,
        title: values.title || "",
        patient: { id: Number(id) },
      })
      .then((res) => {
        setData((prev) => [res.data, ...prev]);
        setVisible(false);
        form.resetFields();
        onTabCount?.("visits", 1);
        message.success("سەردانی پێویست تۆمارکرا");
      })
      .catch(() => {
        message.error("نەتوانرا سەردان تۆمار بکرێت");
      })
      .finally(() => {
        setConfirmLoading(false);
      });
  };

  const remove = (rec) => {
    axios.delete(`${API_BASE_URL}history/${rec.id}`).then(() => {
      setData((prev) => prev.filter((item) => item.id !== rec.id));
      onTabCount?.("visits", -1);
    });
  };

  return (
    <div>
      <Row className="tab-head" style={{ marginTop: 0, borderRadius: 6, padding: 10, backgroundColor: token.colorBgContainer }}>
        <Title level={4} style={{ margin: 0 }}>
          سەردانکردنی پێویست
        </Title>
        <Button className="tab-head-action" type="primary" onClick={openForm}>
          زیادکردن
        </Button>
      </Row>

      <div className="apt-list">
        {data.length === 0 ? <div className="apt-empty">هیچ سەردانێکی پێویست نییە.</div> : null}
        {data.map((item) => (
          <article key={item.id} className="apt-card">
            <div className="apt-card-top">
              <div className="apt-step apt-step-visit">
                <div>
                  <div className="apt-step-label">سەردانکردنی پێویست</div>
                  <div className="apt-step-day">{item.date ? weekdayKu(item.date) : "—"}</div>
                  <div className="apt-step-date">{item.date ? dayjs(item.date).format("YYYY/MM/DD") : "—"}</div>
                </div>
                {item.visitTime ? <span className="apt-time">{formatTime(item.visitTime)}</span> : null}
              </div>
              <Popconfirm title="دڵنیایت لە سڕینەوە؟" okText="بەڵێ" cancelText="نەخێر" onConfirm={() => remove(item)}>
                <Button danger type="text" icon={<DeleteOutlined />} />
              </Popconfirm>
            </div>
            <p className="apt-note">{item.title || "—"}</p>
          </article>
        ))}
      </div>

      <Drawer
        title="زیادکردنی سەردانکردنی پێویست"
        placement="bottom"
        height="100%"
        open={visible}
        onClose={() => setVisible(false)}
        destroyOnClose
        autoFocus={false}
        styles={{
          body: { padding: 16, overflow: "auto" },
          footer: { padding: "16px 12px" },
        }}
        footer={
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Button size="large" onClick={() => setVisible(false)}>پاشگەزبوونەوە</Button>
            <Button size="large" type="primary" loading={confirmLoading} onClick={() => form.submit()}>
              تۆمارکردن
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={submit}>
          {visitWindow ? (
            <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
              قالب {formatDay(visitWindow.qalib)} — تەقەڵ {formatDay(visitWindow.taqal)}. ڕۆژەکانی نێوانیان نیشانکراون.
            </Text>
          ) : null}
          <Form.Item name="date" label="بەروار" rules={[{ required: true, message: "بەروار پێویستە" }]}>
            <AptDatePicker
              placeholder="بەروار هەڵبژێرە"
              markDay={(current) => (isBetweenVisits(current, visitWindow?.qalib, visitWindow?.taqal) ? "clinic" : "")}
            />
          </Form.Item>
          <Form.Item name="visitTime" label="کات" rules={[{ required: true, message: "کات هەڵبژێرە" }]}>
            <AptTimePicker />
          </Form.Item>
          <Form.Item name="title" label="تێبینی بۆ چارەخواز">
            <Input.TextArea rows={4} placeholder="تێبینی بۆ چارەخواز" />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}

export default Visit;
